import { FS } from '@dadohr/shared/versions';
import { Button, Dialog, DialogContent } from '@material-ui/core';
import { capitalize } from 'lodash';
import { ExternalLink, Settings } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { DialogActionsHeader } from '../../../../components/DialogActionsHeader';
import { PageLoader } from '../../../../components/Loader';
import { OrgLogo } from '../../../../components/OrgLogo';
import { DateTimeFieldNullable } from '../../../../components/Time/DateTimeField';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { useQueryParam, useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { toExperienceTemplateDoc } from '../../../../services/experiences/documents';
import { store } from '../../../../services/firebase';
import { tsFromMillis } from '../../../../services/time';
import { AppThemeProviderWithOptionalCustomizations } from '../../../../themes/provider';
import { StandardDrawer } from '../../components/StandardDrawer';
import {
  CustomizePreviewBody,
  CustomizePreviewHeader,
  ExperiencePreviewV2Body
} from '../Details/ExperiencePreview';
import { useExperiencePreviewFromToken } from './service';

const CustomizePreviewDrawerSimple = ({
  label,
  value,
  onChange,
  onClose,
  open,
  tz
}: {
  label: string;
  value: string;
  onChange: (nextValue: string) => void;
  onClose: () => void;
  open: boolean;
  tz: string;
}) => {
  const ts = useMemo(() => {
    return value ? tsFromMillis(new Date(value).valueOf()) : null;
  }, [value]);
  return (
    <StandardDrawer open={open} onClose={onClose}>
      <CustomizePreviewHeader onClose={onClose} />
      <CustomizePreviewBody>
        <DateTimeFieldNullable
          label={capitalize(label)}
          value={ts}
          onChange={(nextTs) => {
            if (nextTs) {
              onChange(nextTs.toDate().toISOString());
              onClose();
            }
          }}
          tz={tz}
          fullWidth
          minutes
        />
      </CustomizePreviewBody>
    </StandardDrawer>
  );
};

export const ExperiencePreview = ({
  previewToken
}: {
  previewToken: string;
}) => {
  //const previewToken = 'g9crAzyiX';
  const { t } = useUnsafeTranslation();
  const [anchorDateIsoString, setAnchorDateIsoString] = useQueryParam<string>(
    'anchorDate',
    (p) => p || '',
    (p) => p || ''
  );

  const customizePreviewDrawer = useDialogState();

  const [experiencePreview, loading, error] = useExperiencePreviewFromToken(
    previewToken,
    anchorDateIsoString
  );

  const currentUser = useCurrentUser();
  const { ROUTES, goTo } = useRoutes();

  const [templateId, setTemplateId] = useState<string>();

  useEffect(() => {
    const getTemplateId = async () => {
      const querySnap = await store()
        .collection(FS.experienceTemplates)
        .where('orgId', '==', currentUser.org.id)
        .where('previewToken.token', '==', previewToken)
        .get();

      if (querySnap.empty) {
        console.error('Template not found for preview token');
      }

      const templates = querySnap.docs.map(toExperienceTemplateDoc);
      if (templates.length > 1) {
        //this should not happne
        console.error('Multiple templates  found for preview token', {
          previewToken,
          templates
        });
      }

      const template = templates[0];
      setTemplateId(template.id);
    };
    if (currentUser) {
      getTemplateId();
    }
  }, [previewToken, currentUser]);

  if (loading) {
    return <PageLoader />;
  }
  if (error) {
    return (
      <Dialog open={true} fullScreen>
        <DialogContent>
          <Centered height={500}>
            {t('This preview is not available at the moment.')}
          </Centered>
        </DialogContent>
      </Dialog>
    );
  }
  if (!experiencePreview) {
    return null;
  }
  const goToAdminView = async () => {
    try {
      if (!currentUser || !templateId) {
        return;
      }

      const experienceURL = ROUTES.experiences.details.general.url(templateId);
      const searchParams = new URLSearchParams();
      searchParams.set('preview', 'e');
      goTo(`${experienceURL}?${searchParams.toString()}`);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <AppThemeProviderWithOptionalCustomizations
      theme={experiencePreview.org.experienceTheming}
    >
      <Dialog open={true} fullScreen>
        <DialogActionsHeader left={<OrgLogo d={experiencePreview.org} />}>
          {currentUser && (
            <Button
              variant="outlined"
              disabled={!templateId}
              startIcon={<ExternalLink size={16} />}
              onClick={goToAdminView}
            >
              {t('View in Admin app')}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<Settings size={16} />}
            onClick={customizePreviewDrawer.openDialog}
          >
            {t('Customize Preview')}
          </Button>
        </DialogActionsHeader>
        <DialogContent>
          <ExperiencePreviewV2Body experiencePreview={experiencePreview} />
        </DialogContent>
      </Dialog>
      <CustomizePreviewDrawerSimple
        open={customizePreviewDrawer.dialogOpen}
        onClose={customizePreviewDrawer.closeDialog}
        label={experiencePreview.anchorName || t('Experience Start')}
        value={anchorDateIsoString}
        onChange={setAnchorDateIsoString}
        tz={experiencePreview.tz}
      />
    </AppThemeProviderWithOptionalCustomizations>
  );
};
